'use client';

import { BRAND, withBase } from '../lib/brand';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { MegaMenuWrapper } from './MegaMenuWrapper';
import { useReviewerAuth } from '../contexts/ReviewerAuthContext';
import ReviewerLoginPrompt from './ReviewerLoginPrompt';
import SearchDropdown from './SearchDropdown';
import CountrySwitcher from './CountrySwitcher';
import LanguageSwitcher from './LanguageSwitcher';
import { isSharePagePath } from '../lib/sharePage';
import dynamic from 'next/dynamic';
import { useCaPreview } from '../lib/caPreview';

// AAA store header (ca_preview only). Its own chunk + CSS, so production
// visitors download none of it while the flag is off.
const AaaHeader = dynamic(() => import('./store/AaaHeader'), { ssr: false });

/**
 * The logo's link home. On Camping Australia "/" is the Shopify store, not a
 * route of this app (it is served under /apps/campsite-explorer with no
 * basePath), so a next/link here prefetched `/?_rsc` from Shopify the moment
 * the logo was on screen: 397 KB of storefront HTML on every page view that
 * the router cannot use (traced 5 Oct 2026). A plain anchor goes to the same
 * place with no prefetch. Escape To serves "/" itself, so it keeps Link.
 */
function HomeLogoLink({ children }: { children: React.ReactNode }) {
  if (BRAND.key === 'camping-australia') {
    return <a href="/" className="logo">{children}</a>;
  }
  return <Link href="/" className="logo">{children}</Link>;
}

/** The red build badge renders only when NEXT_PUBLIC_SHOW_BUILD_BADGE=1. */
export function showBuildBadge(flag: string | undefined = process.env.NEXT_PUBLIC_SHOW_BUILD_BADGE): boolean {
  return flag === '1';
}

const StoreHeader: React.FC = () => {
  const router = useRouter();
  const t = useTranslations('header');
  const { reviewer, isLoggedIn, logout } = useReviewerAuth();
  const [cartCount, setCartCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  // One state per search box, NOT one shared between them. This component
  // mounts two SearchDropdowns — desktop and mobile — and only one is ever
  // visible (CSS display, not conditional rendering). A single flag opened
  // both, and each instance registers its own document-mousedown
  // "click outside to close" listener against its own panel. Pressing a
  // result in the visible panel is outside the hidden one, so the hidden
  // instance closed the pair mid-mousedown; the row unmounted before mouseup,
  // no click event was ever generated on it, and selecting a result did
  // nothing. Measured live: mousedown landed on the result, mouseup landed on
  // the nav bar underneath.
  const [showDesktopSearch, setShowDesktopSearch] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  // Per-instance keyboard handlers. These were a single module-level static on
  // SearchDropdown, which whichever instance mounted last overwrote — so the
  // hidden mobile dropdown handled the desktop box's arrow keys and Enter.
  const desktopKeyHandlerRef = useRef<((e: React.KeyboardEvent) => void) | null>(null);
  const mobileKeyHandlerRef = useRef<((e: React.KeyboardEvent) => void) | null>(null);

  // Deployment version - UPDATE THIS before each deployment
  const DEPLOYMENT_VERSION = 'v1.0.0';
  // Build-time constant (next.config.js). MUST NOT be new Date() at render time:
  // SSR/cached HTML vs client dates diverge on a date rollover → hydration crash.
  const DEPLOYMENT_DATE = process.env.NEXT_PUBLIC_BUILD_DATE || '';

  useEffect(() => {
    // Log deployment info to console
    console.log(`Camping Australia App Deployed`);
    console.log(`Version: ${DEPLOYMENT_VERSION}`);
    console.log(`Date: ${DEPLOYMENT_DATE}`);
    console.log(`Build: ${process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA?.substring(0, 7) || 'local'}`);

    // Fetch cart count from Shopify (camping-australia brand only)
    const fetchCartCount = async () => {
      try {
        const response = await fetch('https://campingaustralia.com.au/cart.js');
        const cartData = await response.json();
        setCartCount(cartData.item_count || 0);
      } catch (error) {
        console.error('Error fetching cart count:', error);
        setCartCount(0);
      }
    };

    if (BRAND.showShopifyGear) fetchCartCount();

    // Listen for cart updates
    const handleCartUpdate = (event: any) => {
      if (event.detail && typeof event.detail.cartCount === 'number') {
        setCartCount(event.detail.cartCount);
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  // Close account dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target as Node)) {
        setShowAccountMenu(false);
      }
    };
    if (showAccountMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAccountMenu]);

  // ── Search handler ─────────────────────────────────────────
  const handleSearchSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // Both boxes share `searchQuery`, so a submit from either should leave
      // neither dropdown open behind the navigation.
      setShowDesktopSearch(false);
      setShowMobileSearch(false);
      const query = searchQuery.trim();
      if (!query) return;

      // Navigate to campsite explorer with location param
      // Must use window.location because router.push('/') goes to Shopify root,
      // not the Next.js app root (mounted at /apps/campsite-explorer via proxy)
      window.location.href = withBase(`?location=${encodeURIComponent(query)}`);
    },
    [searchQuery, router],
  );

  // ── Account click handler ──────────────────────────────────
  const handleAccountClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (isLoggedIn) {
        setShowAccountMenu((prev) => !prev);
      } else {
        setShowLoginModal(true);
      }
    },
    [isLoggedIn],
  );

  // Reviewer display name
  const displayName = reviewer?.display_name
    ? reviewer.display_name.split(' ')[0]
    : reviewer?.email?.split('@')[0] || t('account');

  return (
    <>
      {/* Deployment version badge. Opt-in only (NEXT_PUBLIC_SHOW_BUILD_BADGE=1):
          it used to show on every dev server and on ?debug=true, where it sat
          on top of the header on both viewports and wrapped on mobile. */}
      {showBuildBadge() && (
        <div
          style={{
            position: 'fixed',
            top: '0',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#ff6b6b',
            color: 'white',
            padding: '4px 12px',
            fontSize: '11px',
            fontWeight: 'bold',
            zIndex: 99999,
            borderRadius: '0 0 8px 8px',
            fontFamily: 'monospace',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          APP v1.0.1 | Deploy: {DEPLOYMENT_DATE}
        </div>
      )}

      {/* Desktop header */}
      <div className="header desktop-header" style={{background: '#ffffff', boxShadow: '0 2px 10px rgba(0,0,0,0.1)'}}>
        <div className="bar">
          <div className="logo-container">
            <HomeLogoLink>
              {BRAND.key === 'camping-australia' ? (
                <img
                  src="https://campingaustralia.com.au/cdn/shop/files/ca_website_logo_400x.png?v=1697249902"
                  alt="Camping Australia"
                  width="220"
                  height="110"
                  loading="eager"
                />
              ) : (
                <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--brand-dark)' }}>
                  Escape<span style={{ color: 'var(--brand-primary)' }}>To</span>
                </span>
              )}
            </HomeLogoLink>
          </div>

          <div className="search-container" style={{ position: 'relative' }}>
            <form className="search-form" onSubmit={handleSearchSubmit}>
              <div className="search-input-wrapper">
                <input
                  type="search"
                  name="q"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowDesktopSearch(e.target.value.length >= 2);
                  }}
                  onFocus={() => { if (searchQuery.length >= 2) setShowDesktopSearch(true); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setShowDesktopSearch(false);
                    if (showDesktopSearch && desktopKeyHandlerRef.current) {
                      desktopKeyHandlerRef.current(e);
                    }
                  }}
                  placeholder={BRAND.showShopifyGear ? t('searchPlaceholderShop') : t('searchPlaceholder')}
                  aria-label={BRAND.showShopifyGear ? t('searchAriaShop') : t('searchAria')}
                  autoComplete="off"
                />
                <button type="submit" aria-label={t('searchAria')}>
                  <i className="fc fc-search"></i>
                </button>
              </div>
            </form>
            <SearchDropdown
              query={searchQuery}
              isOpen={showDesktopSearch}
              onClose={() => setShowDesktopSearch(false)}
              keyHandlerRef={desktopKeyHandlerRef}
            />
          </div>

          <div className="icons-container">
            {/* EscapeTo only — country + language selectors. Language is an
                axis independent of country (see LanguageSwitcher). Never on
                camping-australia. */}
            {BRAND.key === 'escapeto' && <LanguageSwitcher />}
            {BRAND.key === 'escapeto' && <CountrySwitcher />}
            <div ref={accountMenuRef} style={{ position: 'relative' }}>
              <a
                className="icon-user"
                href="#"
                onClick={handleAccountClick}
              >
                <i className="fc fc-user"></i>
                <span>{isLoggedIn ? displayName : t('account')}</span>
              </a>

              {/* Logged-in account dropdown */}
              {showAccountMenu && isLoggedIn && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    minWidth: '200px',
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    zIndex: 1000,
                    padding: '8px 0',
                    marginTop: '4px',
                  }}
                >
                  <div style={{ padding: '8px 16px', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                      {reviewer?.display_name || t('userFallback')}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {reviewer?.email || ''}
                    </div>
                  </div>
                  <Link
                    href={withBase("/saved")}
                    style={{
                      display: 'block',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#374151',
                      textDecoration: 'none',
                    }}
                    onClick={() => setShowAccountMenu(false)}
                  >
                    {t('savedTrips')}
                  </Link>
                  <Link
                    href={withBase("/?plan=1")}
                    style={{
                      display: 'block',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#1a68d1',
                      fontWeight: 600,
                      textDecoration: 'none',
                    }}
                    onClick={() => setShowAccountMenu(false)}
                  >
                    {t('planTrip')}
                  </Link>
                  <Link
                    href={withBase("/account/vehicles")}
                    style={{
                      display: 'block',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#374151',
                      textDecoration: 'none',
                    }}
                    onClick={() => setShowAccountMenu(false)}
                  >
                    {t('myVehicles')}
                  </Link>
                  <Link
                    href={withBase("/account/alerts")}
                    style={{
                      display: 'block',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#374151',
                      textDecoration: 'none',
                    }}
                    onClick={() => setShowAccountMenu(false)}
                  >
                    {t('myAlerts')}
                  </Link>
                  <Link
                    href={withBase("/my-reviews")}
                    style={{
                      display: 'block',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#374151',
                      textDecoration: 'none',
                    }}
                    onClick={() => setShowAccountMenu(false)}
                  >
                    {t('myReviews')}
                  </Link>
                  <Link
                    href={withBase("/recommend-campsite")}
                    style={{
                      display: 'block',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#374151',
                      textDecoration: 'none',
                    }}
                    onClick={() => setShowAccountMenu(false)}
                  >
                    {t('suggestCampsite')}
                  </Link>
                  {BRAND.showShopifyGear && (
                    <a
                      href="https://campingaustralia.com.au/account"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        padding: '8px 16px',
                        fontSize: '14px',
                        color: '#374151',
                        textDecoration: 'none',
                      }}
                      onClick={() => setShowAccountMenu(false)}
                    >
                      {t('shopAccount')}
                    </a>
                  )}
                  <button
                    onClick={() => {
                      logout();
                      setShowAccountMenu(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#dc2626',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      borderTop: '1px solid #f3f4f6',
                    }}
                  >
                    {t('signOut')}
                  </button>
                </div>
              )}
            </div>
            {BRAND.showShopifyGear && (
              <a className="icon-cart" href="https://campingaustralia.com.au/cart" target="_blank">
                <i className="fc fc-cart"></i>
                <span>{t('cart')}</span>
                <span className={`cart-count ${cartCount === 0 ? 'hidden-count' : ''}`}>
                  {cartCount}
                </span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Mobile header */}
      <div className="mobile-header">
        <button className="toggle-menu" aria-label="Menu">
          <i className="fc fc-hamburguer"></i>
        </button>

        <div className="logo-container">
          <HomeLogoLink>
            {BRAND.key === 'camping-australia' ? (
              // height reserves the logo's box before it loads. The file is
              // 250x125; without a height the mobile header grew by 72px when
              // it arrived and pushed the whole page down (Lighthouse CLS
              // 0.085 on <main>, 5 Oct 2026).
              <img
                src="https://campingaustralia.com.au/cdn/shop/files/ca_website_logo_400x.png?v=1697249902"
                alt="Camping Australia"
                width="144"
                height="72"
              />
            ) : (
              <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--brand-dark)' }}>
                Escape<span style={{ color: 'var(--brand-primary)' }}>To</span>
              </span>
            )}
          </HomeLogoLink>
        </div>

        <div className="icons-container">
          {BRAND.key === 'escapeto' && <LanguageSwitcher />}
          {BRAND.key === 'escapeto' && <CountrySwitcher />}
          <a className="icon-user" href="#" onClick={handleAccountClick}>
            <i className="fc fc-user"></i>
          </a>
          {BRAND.showShopifyGear && (
            <a className="icon-cart" href="https://campingaustralia.com.au/cart" target="_blank">
              <i className="fc fc-cart"></i>
              <span className={`cart-count ${cartCount === 0 ? 'hidden-count' : ''}`}>
                {cartCount}
              </span>
            </a>
          )}
        </div>
      </div>

      {/* Mobile search */}
      <div className="mobile-search" style={{ position: 'relative' }}>
        <form className="search-form" onSubmit={handleSearchSubmit}>
          <div className="search-input-wrapper">
            <input
              type="search"
              name="q"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowMobileSearch(e.target.value.length >= 2);
              }}
              onFocus={() => { if (searchQuery.length >= 2) setShowMobileSearch(true); }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setShowMobileSearch(false);
                if (showMobileSearch && mobileKeyHandlerRef.current) {
                  mobileKeyHandlerRef.current(e);
                }
              }}
              placeholder={BRAND.showShopifyGear ? t('searchPlaceholderShop') : t('searchPlaceholder')}
              aria-label={BRAND.showShopifyGear ? t('searchAriaShop') : t('searchAria')}
              autoComplete="off"
            />
            <button type="submit" aria-label={t('searchAria')}>
              <i className="fc fc-search"></i>
            </button>
          </div>
        </form>
        <SearchDropdown
          query={searchQuery}
          isOpen={showMobileSearch}
          onClose={() => setShowMobileSearch(false)}
          keyHandlerRef={mobileKeyHandlerRef}
        />
      </div>

      {/* USP Trust Bar — Shopify store promises (camping-australia only) */}
      {BRAND.showShopifyGear && (
      <div className="usp-bar" aria-label="Key benefits">
        <div className="usp-bar__inner">
          <div className="usp-bar__item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="1 4 1 11 8 11"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/></svg>
            <span>30-day returns</span>
          </div>
          <div className="usp-bar__divider" aria-hidden="true"></div>
          <div className="usp-bar__item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <span>Secure checkout</span>
          </div>
          <div className="usp-bar__divider" aria-hidden="true"></div>
          <div className="usp-bar__item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span>Australian owned &amp; operated</span>
          </div>
          <div className="usp-bar__divider usp-bar__divider--hide-mobile" aria-hidden="true"></div>
          <div className="usp-bar__item usp-bar__item--hide-mobile">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.9A8 8 0 1 1 21 12z"/></svg>
            <a href="/pages/contact-us#chat" style={{ color: 'inherit', textDecoration: 'none' }}>Expert advice · chat with us</a>
          </div>
        </div>
      </div>

      )}

      {/* THIS IS WHERE MEGAMENUWRAPPER IS USED - AT THE VERY END */}
      {BRAND.showShopifyGear && <MegaMenuWrapper />}

      {/* Login modal — shown when unauthenticated user clicks Account */}
      <ReviewerLoginPrompt
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        title={t('signInTitle')}
        subtitle={t('signInSubtitle')}
      />
    </>
  );
};

/**
 * A shared trip is something you forward, not a shop page: one 56px bar with
 * the logo and a way into the planner. No trust bar, no mega-nav.
 */
function SlimHeader() {
  return (
    <header
      data-testid="slim-header"
      style={{ height: 56, background: '#fff', borderBottom: '1px solid #e6ebf1', position: 'relative', zIndex: 40 }}
    >
      <div
        style={{
          height: '100%', maxWidth: 1280, margin: '0 auto', padding: '0 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}
      >
        <HomeLogoLink>
          {BRAND.key === 'camping-australia' ? (
            <img
              src="https://campingaustralia.com.au/cdn/shop/files/ca_website_logo_400x.png?v=1697249902"
              alt="Camping Australia"
              width="72"
              height="36"
              style={{ display: 'block', height: 36, width: 'auto' }}
              loading="eager"
            />
          ) : (
            <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: 22, color: 'var(--brand-dark)' }}>
              Escape<span style={{ color: 'var(--brand-primary)' }}>To</span>
            </span>
          )}
        </HomeLogoLink>
        {/* Quiet on purpose: the page's own "Take this trip" is the one call to action. */}
        <a
          href={withBase('/?plan=1')}
          style={{
            display: 'inline-flex', alignItems: 'center', minHeight: 0, height: 36, padding: '0 4px',
            fontSize: 14, fontWeight: 500, color: '#5b6b82', textDecoration: 'none',
          }}
          className="hover:text-[#16233a] hover:underline underline-offset-4"
        >
          Plan a trip
        </a>
      </div>
    </header>
  );
}

const Header: React.FC = () => {
  const pathname = usePathname();
  const preview = useCaPreview();
  if (isSharePagePath(pathname)) return <SlimHeader />;
  return preview ? <AaaHeader /> : <StoreHeader />;
};

export default Header;
